import { BarChart3Icon, DownloadIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";

const ImpactReport = () => {
  const { toast } = useToast();
  
  const handleDownload = () => {
    // Use the server endpoint that redirects to Google Drive
    window.open('/download/impact-report', '_blank');
    
    toast({
      title: "Impact Report Opening",
      description: "StaffAbility: Impact and Opportunity Report is opening in a new tab.",
      duration: 3000,
    });
  };

  return (
    <div className="py-12 md:py-20 bg-white">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <div className="flex items-center justify-center mb-4">
            <div className="bg-blue-100 rounded-lg p-2 text-blue-700 mr-3">
              <BarChart3Icon className="h-6 w-6" />
            </div>
            <h1 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              Impact and Opportunity Report
            </h1>
          </div>
          <p className="mt-4 text-xl text-gray-600">
            Data-Driven Insights on StaffAbility's Potential Impact
          </p>
        </div>

        <div className="bg-blue-50 p-6 rounded-xl shadow-sm mb-10 border border-blue-200">
          <h2 className="text-xl font-semibold text-blue-900">Executive Summary</h2>
          <p className="mt-4 text-gray-700">
            StaffAbility is a nonprofit initiative dedicated to reducing recidivism and improving economic opportunities
            for justice-involved individuals through employment-focused support. This report presents a
            comprehensive analysis of StaffAbility's potential impact, integrating data science, policy insights, and social
            impact considerations.
          </p>
          <p className="mt-4 text-gray-700">
            Key findings indicate that stable employment is one of the strongest factors in
            preventing re-offense: national data show nearly two-thirds of released prisoners are rearrested within
            3–5 years, yet those who secure and maintain employment see dramatically lower recidivism rates (as
            low as 16% versus 52% for the unemployed).
          </p>
        </div>

        <div className="prose prose-blue prose-lg max-w-none mb-16">
          <h2 className="text-2xl font-bold text-blue-900">Economic Impact Analysis</h2>
          <p>
            Beyond direct cost savings, there are positive economic multipliers when formerly incarcerated people
            secure employment. Instead of consuming public resources, they begin to contribute to the economy. A
            Philadelphia study estimated that employing 100 formerly incarcerated individuals would increase
            their collective lifetime earnings by $55 million.
          </p>
          <p>
            These earnings translate into greater consumer spending and economic growth in their communities. 
            Moreover, higher incomes generate more tax revenue: those same 100 people would contribute an 
            additional $1.9 million in income taxes and $770,000 in sales taxes over their lifetimes.
          </p>

          <h2 className="text-2xl font-bold text-blue-900 mt-10">Key Findings</h2>
          <h3 className="text-xl font-semibold text-gray-800">Employment & Recidivism</h3>
          <ul>
            <li>Employment decreases the likelihood of reoffending by up to 20%</li>
            <li>Individuals with steady employment show a 16% recidivism rate vs. 52% for the unemployed</li>
            <li>Each person kept out of prison saves roughly $30,000 per year in incarceration costs</li>
            <li>Every dollar invested in prison education saves four to five dollars in reincarceration costs</li>
            <li>Work release programs demonstrate an ROI of $11.19 for every $1.00 spent</li>
          </ul>
          
          <h3 className="text-xl font-semibold text-gray-800">Economic Benefits</h3>
          <ul>
            <li>StaffAbility's model could cut recidivism rates by half or more</li>
            <li>Each cohort of 100 individuals could save $750,000 annually in reduced incarceration costs</li>
            <li>By 2030, StaffAbility aims to graduate 50,000+ individuals from its program</li>
            <li>Projected economic impact of $720 million through reduced recidivism and increased tax contributions</li>
            <li>Staff retention rates for second-chance hires are 83% at one year vs. industry average of 58%</li>
          </ul>

          <h2 className="text-2xl font-bold text-blue-900 mt-10">Program Metrics & Impact</h2>
          <h3 className="text-xl font-semibold text-gray-800">Projected Outcomes</h3>
          <p>
            Based on our comprehensive economic analysis and pilot data, StaffAbility aims to achieve the following
            outcomes within five years of full implementation:
          </p>
          <ul>
            <li><strong>Employment Rate:</strong> 78% of participants employed within 90 days of program completion</li>
            <li><strong>Job Retention:</strong> 83% retention rate at one year (vs. industry average of 58%)</li>
            <li><strong>Income Growth:</strong> 45% increase in average income from program entry to one year post-completion</li>
            <li><strong>Recidivism Reduction:</strong> 62% lower recidivism rate compared to similar non-participating individuals</li>
            <li><strong>ROI for Communities:</strong> Every $1 invested returns $12.50 in reduced social costs and economic benefits</li>
          </ul>

          <h2 className="text-2xl font-bold text-blue-900 mt-10">Regional Impact Potential</h2>
          <p>
            StaffAbility's model is designed to be adaptable to various regions, with particular focus on areas with
            high recidivism rates and workforce needs. States like Delaware and Rhode Island, with recidivism rates
            of 64% and 49% respectively, stand to benefit significantly from this approach.
          </p>
          <p>
            By specifically targeting these high-impact regions, StaffAbility can maximize its effectiveness and
            create meaningful change where it's needed most. The report includes targeted analysis of potential
            implementation in these regions, with customized projections and strategies.
          </p>

          <h2 className="text-2xl font-bold text-blue-900 mt-10">Download the Full Report</h2>
          <p>
            For a comprehensive analysis including detailed methodology, additional statistics, and regional impact
            assessments, download the complete Impact and Opportunity Report using the button below.
          </p>
          
          <div className="flex justify-center mt-8 mb-4">
            <Button size="lg" className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700" onClick={handleDownload}>
              <DownloadIcon className="h-5 w-5" />
              Download Full Impact Report (PDF)
            </Button>
          </div>
          <p className="text-center text-sm text-gray-500">
            PDF format, 1.2MB
          </p>
        </div>
      </div>
    </div>
  );
};

export default ImpactReport;